import localforage from 'localforage'
import { syncAPI } from 'api/sync'
import { getAppVersionLabel } from 'helpers/appVersion'
import { getOrCreatePosDeviceId } from 'utils/posDeviceId'
import { generateClientUuid } from 'utils/clientUuid'
import { resolveOfflineScope } from 'store/posStore'

const auditStore = localforage.createInstance({
  name: 'erp-pos',
  storeName: 'pos',
})

const AUDIT_KEY = 'pos_audit_events'
const AUDIT_LOCK_NAME = 'erp-pos:checkout-audit:v1'
const MAX_EVENTS = 5_000
const MAX_UPLOAD_BATCH = 500

export type PosAuditEvent =
  | 'created'
  | 'intent_prepared'
  | 'persisted'
  | 'cart_consumed'
  | 'print_started'
  | 'push_started'
  | 'push_confirmed'
  | 'push_failed'
  | 'persistence_lost'
  | 'printed'
  | 'dismissed'
  | 'reconcile_attempt'
  | 'app_recovered'

export type PosAuditEntry = {
  event_id: string
  client_uuid: string
  event: PosAuditEvent
  at: string
  device_id: string
  client_version: string
  meta?: Record<string, unknown>
}

let auditMutationTail: Promise<void> = Promise.resolve()

function mutateAuditInTab<T>(callback: () => Promise<T>): Promise<T> {
  const run = auditMutationTail.then(callback, callback)
  auditMutationTail = run.then(() => undefined, () => undefined)
  return run
}

async function mutateAudit<T>(callback: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
  if (locks?.request) {
    return await locks.request(
      AUDIT_LOCK_NAME,
      { mode: 'exclusive' },
      async () => await mutateAuditInTab(callback),
    )
  }
  return await mutateAuditInTab(callback)
}

export async function logPosAuditEvent(
  clientUuid: string,
  event: PosAuditEvent,
  meta?: Record<string, unknown>,
): Promise<void> {
  if (!clientUuid) return
  const currentBranchId = resolveOfflineScope().branchId
  const branchId = typeof meta?.branch_id === 'string' && meta.branch_id.trim() !== ''
    ? meta.branch_id
    : currentBranchId !== 'unknown'
      ? currentBranchId
      : null
  const entry: PosAuditEntry = {
    event_id: generateClientUuid(),
    client_uuid: clientUuid,
    event,
    at: new Date().toISOString(),
    device_id: getOrCreatePosDeviceId(),
    client_version: getAppVersionLabel().replace(/^v/, ''),
    meta: branchId ? { ...(meta ?? {}), branch_id: branchId } : meta,
  }
  await mutateAudit(async () => {
    const raw = (await auditStore.getItem<PosAuditEntry[]>(AUDIT_KEY)) ?? []
    const next = [...raw, entry].slice(-MAX_EVENTS)
    await auditStore.setItem(AUDIT_KEY, next)
  })
}

export async function getPosAuditEvents(clientUuid?: string): Promise<PosAuditEntry[]> {
  const raw = (await auditStore.getItem<PosAuditEntry[]>(AUDIT_KEY)) ?? []
  if (!clientUuid) return raw
  return raw.filter((e) => e.client_uuid === clientUuid)
}

export async function getPosAuditTrailForClient(clientUuid: string): Promise<PosAuditEntry[]> {
  return getPosAuditEvents(clientUuid)
}

/** Best-effort durable upload. Local events remain until the server acknowledges them. */
export async function flushPosAuditEvents(): Promise<number> {
  const snapshot = await getPosAuditEvents()
  if (snapshot.length === 0) return 0
  const byBranch = new Map<string, PosAuditEntry[]>()
  for (const entry of snapshot) {
    const branchId = entry.meta?.branch_id
    if (typeof branchId !== 'string' || branchId.length === 0) continue
    byBranch.set(branchId, [...(byBranch.get(branchId) ?? []), entry])
  }
  if (byBranch.size === 0) return 0
  const uploadedIds = new Set<string>()
  for (const [branchId, entries] of Array.from(byBranch.entries())) {
    for (let offset = 0; offset < entries.length; offset += MAX_UPLOAD_BATCH) {
      const batch = entries.slice(offset, offset + MAX_UPLOAD_BATCH)
      await syncAPI.postPosAuditEvents(batch, branchId)
      batch.forEach((entry) => uploadedIds.add(entry.event_id))
    }
  }
  await mutateAudit(async () => {
    const latest = (await auditStore.getItem<PosAuditEntry[]>(AUDIT_KEY)) ?? []
    await auditStore.setItem(AUDIT_KEY, latest.filter((entry) => !uploadedIds.has(entry.event_id)))
  })
  return uploadedIds.size
}
